// File: src/pages/buyer/AddProduct/sections/PricingEstimation.jsx
import React, { useState } from "react";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import { Row, Col, Form, Button } from "react-bootstrap";
import {
  formatWithCommasAndDecimals,
  parseRawNumber,
} from "../../../../utils/price";

const PricingEstimation = ({ calculatePrice, subCategories = [] }) => {
  const {
    control,
    register,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext();

  const estimated = useWatch({ control, name: "estimated_price" });
  const pricePerUnit = useWatch({ control, name: "price_per_unit" });

  const calcUnit = useWatch({ control, name: "calc_unit" });
  const currency = useWatch({ control, name: "currency" });

  const getDynamicLabel = (baseLabel, showPricePerUnit = false) => {
    if (!calcUnit || !currency) return baseLabel;
    const ratePart =
      showPricePerUnit && pricePerUnit
        ? `Price per unit: ${formatWithCommasAndDecimals(pricePerUnit)} `
        : "";
    return (
      <>
        {baseLabel}
        <span
          className="text-muted ms-2"
          style={{ fontWeight: "400", fontSize: "0.85rem" }}
        >
          ({ratePart}
          {currency}/{calcUnit})
        </span>
      </>
    );
  };

  // console.log('Subcategories in PricingEstimation:', subCategories);

  const handleEstimate = async () => {
    const vals = getValues();
    if (!vals.subcategory_id || !vals.calc_weight || !vals.calc_unit)
      return;

    const subcategoryName =
      subCategories.find((s) => String(s.id) === String(vals.subcategory_id))
        ?.name || "";

    if (!subcategoryName) {
      console.error("No subcategory name found for id:", vals.subcategory_id);
      // alert("Please select a valid subcategory before calculating price.");
      return;
    }

    const payload = {
      metal: subcategoryName.toLowerCase(),
      weight: vals.calc_weight,
      unit: vals.calc_unit.toLowerCase(),
      currency: vals.currency,
    };

    console.log("Payload being sent:", payload);

    try {
      const result = await calculatePrice(payload);
      console.log("Price calculation result:", result);
      console.log("Price calculation result:", result);

      setValue("estimated_price", result.estimated_price ?? "");
      setValue("price_per_unit", result.price_per_unit ?? "");
    } catch (err) {
      console.error("Calculate price failed:", err);
      // alert(err.payload || "Unable to calculate price for this subcategory.");
      setValue("estimated_price", "");
      setValue("price_per_unit", "");
    }
  };

  return (
    <div className="border rounded p-4 mb-4 bg-white">
      <h5 className="fw-semibold mb-4">Pricing &amp; Estimation</h5>

      <Row className="g-3 align-items-end">
        {/* Weight */}
        <Col md={2}>
          <Form.Group>
            <Form.Label>Weight</Form.Label>
            <Form.Control type="number" {...register("calc_weight")} />
            {errors.calc_weight && (
              <div className="text-danger small mt-1">
                {errors.calc_weight.message}
              </div>
            )}
          </Form.Group>
        </Col>

        {/* Unit */}
        <Col md={2}>
          <Form.Group>
            <Form.Label>Unit</Form.Label>
            <Controller
              name="calc_unit"
              control={control}
              render={({ field }) => (
                <Form.Select {...field}>
                  <option value="">Select Unit</option>
                  <option value="toz">Troy Ounce</option>
                  <option value="mt">Metric Tonne</option>
                  <option value="kg">Kg</option>
                  <option value="g">Gram</option>
                </Form.Select>
              )}
            />
            {errors.calc_unit && (
              <div className="text-danger small mt-1">
                {errors.calc_unit.message}
              </div>
            )}
          </Form.Group>
        </Col>

        {/* Currency */}
        <Col md={2}>
          <Form.Group>
            <Form.Label>Currency</Form.Label>
            <Controller
              name="currency"
              control={control}
              render={({ field }) => (
                <Form.Select
                  {...field}
                  isInvalid={!!errors.currency}
                >
                  <option value="">Select Currency</option>
                  <option value="AUD">AUD</option>
                  <option value="USD">USD</option>
                </Form.Select>
              )}
            />
            {errors.currency && (
              <div className="text-danger small mt-1">
                {errors.currency.message}
              </div>
            )}
          </Form.Group>
        </Col>

        {/* Calculate */}
        <Col md={2}>
          <Button variant="info" className="w-100" onClick={handleEstimate}>
            Calculate
          </Button>
        </Col>

        {/* Estimated Price */}
        <Col md={2}>
          <Form.Group>
            <Form.Label>{getDynamicLabel("Estimated Price")}</Form.Label>
            <Form.Control
              readOnly
              value={
                estimated
                  ? Number(estimated).toLocaleString(undefined, {
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3,
                  })
                  : ""
              }
            />
          </Form.Group>
        </Col>

        {/* Price per Unit */}
        <Col md={2}>
          <Form.Group>
            <Form.Label>{getDynamicLabel("Price per Unit")}</Form.Label>
            <Form.Control
              readOnly
              value={
                pricePerUnit
                  ? Number(pricePerUnit).toLocaleString(undefined, {
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3,
                  })
                  : ""
              }
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Manual Price Input */}
      {/* <Row className="mt-4">
        <Col md={8}>
          <Form.Group>
            <Form.Label>Price (AUD)</Form.Label>
            <Controller
              name="price"
              control={control}
              render={({ field }) => {
                const formattedValue = field.value
                  ? formatWithCommasAndDecimals(field.value)
                  : "";

                return (
                  <Form.Control
                    {...field}
                    value={formattedValue}
                    placeholder="Enter price"
                    onChange={(e) => {
                      const raw = parseRawNumber(e.target.value);
                      field.onChange(raw);
                    }}
                  />
                );
              }}
            />
            {errors.price && (
              <div className="text-danger small mt-1">
                {errors.price.message}
              </div>
            )}
          </Form.Group>
        </Col>
      </Row> */}
    </div>
  );
};

export default React.memo(PricingEstimation);
